import { components } from '@/api/skippay/generated/gcss';
import { parseApiAmount } from '@/utils/amount';
import { QueryFilter } from '@/utils/query';

const SEARCH_CATEGORY = 'categoryId';
const SEARCH_PRICE = 'priceAmount';

export const FIELD_FULLTEXT = 'fulltext';
export const FIELD_SORT = 'sort';
export const FIELD_AVAILABILITY = 'availability';
export const FIELD_PRICE_FROM = 'priceFrom';
export const FIELD_PRICE_TO = 'priceTo';
export const FIELD_BRAND = 'brand';
export const FIELD_IS_ON_SALE = 'isOnSale';
export const FIELD_MERCHANT = 'merchantName';
export const FIELD_PAGE = 'page';

export type SearchFormValuesGCSS = {
	[FIELD_FULLTEXT]?: string;
	[FIELD_SORT]?: components['schemas']['ProductSort'];
	[FIELD_AVAILABILITY]?: components['schemas']['Availability'];
	[FIELD_PRICE_FROM]?: string;
	[FIELD_PRICE_TO]?: string;
	[FIELD_BRAND]?: string[];
	[FIELD_IS_ON_SALE]?: 'true' | 'false';
	[FIELD_MERCHANT]?: string[];
	[FIELD_PAGE]?: string;
};

export type GcssSearchFilters = {
	productFilter?: string;
	brandsFilter?: string;
	merchantsFilter?: string;
	sort?: components['schemas']['ProductSort'];
};

export function buildGcssSearchFilters(
	formValues: SearchFormValuesGCSS,
	categoryId?: number
): GcssSearchFilters {
	let productFilter = new QueryFilter();
	let brandsFilter = new QueryFilter();
	let merchantsFilter = new QueryFilter();
	const brandFormValues = formValues[FIELD_BRAND];
	const merchantFormValues = formValues[FIELD_MERCHANT];

	if (categoryId) {
		productFilter = productFilter.prop(SEARCH_CATEGORY).eq(String(categoryId));
		brandsFilter = brandsFilter.prop(SEARCH_CATEGORY).eq(String(categoryId));
		merchantsFilter = merchantsFilter.prop(SEARCH_CATEGORY).eq(String(categoryId));
	}

	if (formValues[FIELD_FULLTEXT]) {
		productFilter = productFilter.prop(FIELD_FULLTEXT).eq(formValues[FIELD_FULLTEXT]);
		brandsFilter = brandsFilter.prop(FIELD_FULLTEXT).eq(formValues[FIELD_FULLTEXT]);
		merchantsFilter = merchantsFilter.prop(FIELD_FULLTEXT).eq(formValues[FIELD_FULLTEXT]);
	}

	if (formValues[FIELD_AVAILABILITY]) {
		productFilter = productFilter
			.prop(FIELD_AVAILABILITY)
			.eq('IN_STOCK' satisfies components['schemas']['Availability']);
		brandsFilter = brandsFilter
			.prop(FIELD_AVAILABILITY)
			.eq('IN_STOCK' satisfies components['schemas']['Availability']);
		merchantsFilter = merchantsFilter
			.prop(FIELD_AVAILABILITY)
			.eq('IN_STOCK' satisfies components['schemas']['Availability']);
	}

	if (formValues[FIELD_PRICE_FROM]) {
		productFilter = productFilter
			.prop(SEARCH_PRICE)
			.gt(parseApiAmount(formValues[FIELD_PRICE_FROM]));
		brandsFilter = brandsFilter.prop(SEARCH_PRICE).gt(parseApiAmount(formValues[FIELD_PRICE_FROM]));
		merchantsFilter = merchantsFilter
			.prop(SEARCH_PRICE)
			.gt(parseApiAmount(formValues[FIELD_PRICE_FROM]));
	}

	if (formValues[FIELD_PRICE_TO]) {
		productFilter = productFilter.prop(SEARCH_PRICE).lt(parseApiAmount(formValues[FIELD_PRICE_TO]));
		brandsFilter = brandsFilter.prop(SEARCH_PRICE).lt(parseApiAmount(formValues[FIELD_PRICE_TO]));
		merchantsFilter = merchantsFilter
			.prop(SEARCH_PRICE)
			.lt(parseApiAmount(formValues[FIELD_PRICE_TO]));
	}

	if (brandFormValues && brandFormValues.length > 0) {
		const brands = Array.isArray(brandFormValues) ? brandFormValues : [brandFormValues];

		productFilter = productFilter.prop(FIELD_BRAND).in(brands);
		merchantsFilter = merchantsFilter.prop(FIELD_BRAND).in(brands);
	}

	if (merchantFormValues && merchantFormValues.length > 0) {
		const merchants = Array.isArray(merchantFormValues)
			? merchantFormValues.map((merchant) => merchant.replace(' cz', ''))
			: [(merchantFormValues as string).replace(' cz', '')];

		productFilter = productFilter.prop(FIELD_MERCHANT).in(merchants);
		brandsFilter = brandsFilter.prop(FIELD_MERCHANT).in(merchants);
	}

	if (formValues[FIELD_IS_ON_SALE]) {
		productFilter = productFilter.prop(FIELD_IS_ON_SALE).eq(formValues[FIELD_IS_ON_SALE]);
		brandsFilter = brandsFilter.prop(FIELD_IS_ON_SALE).eq(formValues[FIELD_IS_ON_SALE]);
		merchantsFilter = merchantsFilter.prop(FIELD_IS_ON_SALE).eq(formValues[FIELD_IS_ON_SALE]);
	}

	return {
		productFilter: productFilter.conditions.length > 0 ? productFilter.toString() : undefined,
		brandsFilter: brandsFilter.conditions.length > 0 ? brandsFilter.toString() : undefined,
		merchantsFilter: merchantsFilter.conditions.length > 0 ? merchantsFilter.toString() : undefined,
		sort: formValues[FIELD_SORT],
	};
}
